import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import SignatureBuilder from "./pages/SignatureBuilder";
import AdMarketplace from "./pages/AdMarketplace";
import ApprovalCenter from "./pages/ApprovalCenter";
import PaymentSuccess from "./pages/PaymentSuccess";
import Settings from "./pages/Settings";
import AdminDashboard from "./pages/AdminDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import ExtensionPage from "./pages/ExtensionPage";
import CommunityHub from "./pages/CommunityHub";
import Events from "./pages/Events";
import Groups from "./pages/Groups";
import GroupDetailPage from "./pages/GroupDetailPage";
import EventDetailPage from "./pages/EventDetailPage";
import LearningHub from "./pages/LearningHub";
import Directory from "./pages/Directory";
import ConnectCredits from "./pages/ConnectCredits";
import InviteContacts from "./pages/InviteContacts";
import NetworkPage from "./pages/NetworkPage";
import ExplorePage from "./pages/ExplorePage";
import LeaderboardPage from "./pages/LeaderboardPage";
import TutorialsPage from "./pages/TutorialsPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DemoPage from "./pages/DemoPage";
import BusinessMarketplace from "./pages/BusinessMarketplace";
import HiringPage from "./pages/HiringPage";
import { MyBusinessCard, PublicBusinessCard } from "./pages/BusinessCard";
import { InstallPrompt } from "./components/InstallPrompt";
import { OnboardingTour } from "./components/OnboardingTour";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  // If a logged-in user hits /login (e.g. via an email "Review Request"
  // link with ?next=/approvals?request=...), respect the next param and
  // bounce them there immediately instead of dropping them on the dashboard.
  const loginRedirect = (() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get('next') || '';
    if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
    return '/dashboard';
  })();

  return (
    <ErrorBoundary>
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={loginRedirect} replace /> : <LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/signatures" element={<ProtectedRoute><SignatureBuilder /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><AdMarketplace /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute><ApprovalCenter /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><CommunityHub /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
      <Route path="/events/:eventId" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/groups/:groupId" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
      <Route path="/network" element={<ProtectedRoute><NetworkPage /></ProtectedRoute>} />
      <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/tutorials" element={<ProtectedRoute><TutorialsPage /></ProtectedRoute>} />
      <Route path="/business" element={<ProtectedRoute><BusinessMarketplace /></ProtectedRoute>} />
      <Route path="/hiring" element={<ProtectedRoute><HiringPage /></ProtectedRoute>} />
      <Route path="/connection-hub" element={<Navigate to="/explore" replace />} />
      <Route path="/learn" element={<ProtectedRoute><LearningHub /></ProtectedRoute>} />
      <Route path="/directory" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
      <Route path="/connect" element={<ProtectedRoute><ConnectCredits /></ProtectedRoute>} />
      <Route path="/invite-contacts" element={<ProtectedRoute><InviteContacts /></ProtectedRoute>} />
      <Route path="/extension" element={<ProtectedRoute><ExtensionPage /></ProtectedRoute>} />
      <Route path="/business-card" element={<ProtectedRoute><MyBusinessCard /></ProtectedRoute>} />
      <Route path="/card/me" element={<ProtectedRoute><MyBusinessCard /></ProtectedRoute>} />
      <Route path="/card/:userId" element={<PublicBusinessCard />} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
    </Routes>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <InstallPrompt />
        <OnboardingTour />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
